import NavBar from '../components/NavBar';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

const ACTION_META = {
  'user.login':            { icon: '🔑', color: '#00e676', label: 'Login' },
  'user.logout':           { icon: '🚪', color: '#5c6b82', label: 'Logout' },
  'user.created':          { icon: '👤', color: '#00e676', label: 'User Created' },
  'user.deleted':          { icon: '🗑',  color: '#ff4d4d', label: 'User Deleted' },
  'user.updated':          { icon: '✏️',  color: '#f59e0b', label: 'User Updated' },
  'stream.start':          { icon: '🔴', color: '#00e676', label: 'Stream Started' },
  'stream.end':            { icon: '⏹',  color: '#5c6b82', label: 'Stream Ended' },
  'destination.added':     { icon: '➕', color: '#9146ff', label: 'Destination Added' },
  'destination.deleted':   { icon: '➖', color: '#ff4d4d', label: 'Destination Removed' },
  'destination.failed':    { icon: '⚠️', color: '#ff4d4d', label: 'Destination Failed' },
  'recording.completed':   { icon: '🎬', color: '#00e676', label: 'Recording Saved' },
  'recording.deleted':     { icon: '🗑',  color: '#ff4d4d', label: 'Recording Deleted' },
  'settings.changed':      { icon: '⚙️', color: '#f59e0b', label: 'Settings Changed' },
  'apikey.created':        { icon: '🔐', color: '#9146ff', label: 'API Key Created' },
  'apikey.deleted':        { icon: '🗑',  color: '#ff4d4d', label: 'API Key Deleted' },
};

function getActionMeta(action) {
  return ACTION_META[action] || { icon: '•', color: 'var(--text-muted)', label: action };
}

export default function ActivityLog() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset });
      if (filter) params.set('action', filter);
      const { data } = await api.get(`/activity?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch {}
    finally { setLoading(false); }
  }, [filter, offset]);

  useEffect(() => { setOffset(0); }, [filter]);
  useEffect(() => { load(); }, [load]);

  const formatDetail = (detail) => {
    if (!detail) return '';
    if (typeof detail === 'string') return detail;
    const d = typeof detail === 'object' ? detail : JSON.parse(detail);
    return Object.entries(d).map(([k,v]) => `${k}: ${v}`).join(' · ');
  };

  const s = {
    page: { minHeight:'100vh', background:'var(--bg)' },
    nav: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 32px', height:'58px', borderBottom:'1px solid var(--border)', background:'var(--surface)', position:'sticky', top:0, zIndex:10 },
    navLogo: { display:'flex', alignItems:'center', gap:'9px', fontWeight:'800', fontSize:'17px' },
    navDot: { width:'9px', height:'9px', borderRadius:'50%', background:'var(--signal)', boxShadow:'0 0 8px var(--signal)' },
    navRight: { display:'flex', alignItems:'center', gap:'10px' },
    navBtn: (h) => ({ fontSize:'13px', padding:'6px 14px', border:`1px solid ${h?'rgba(0,230,118,0.3)':'var(--border)'}`, borderRadius:'7px', background:'transparent', color:h?'var(--signal)':'var(--text-muted)', cursor:'pointer' }),
    main: { maxWidth:'1000px', margin:'0 auto', padding:'36px 24px' },
    header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'24px', flexWrap:'wrap', gap:'12px' },
    title: { fontSize:'24px', fontWeight:'800', letterSpacing:'-0.5px' },
    filters: { display:'flex', gap:'6px', flexWrap:'wrap' },
    filterBtn: (active) => ({ padding:'5px 12px', borderRadius:'6px', fontSize:'12px', fontWeight:'600', cursor:'pointer', background:active?'var(--signal)':'var(--surface2)', color:active?'#000':'var(--text-muted)', border:`1px solid ${active?'var(--signal)':'var(--border)'}` }),
    logList: { display:'flex', flexDirection:'column', gap:'4px' },
    logRow: { display:'flex', alignItems:'flex-start', gap:'12px', padding:'10px 14px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'8px' },
    logIcon: (color) => ({ width:'28px', height:'28px', borderRadius:'6px', background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', flexShrink:0 }),
    logInfo: { flex:1, minWidth:0 },
    logAction: (color) => ({ fontSize:'13px', fontWeight:'600', color }),
    logDetail: { fontSize:'12px', color:'var(--text-muted)', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
    logMeta: { fontSize:'11px', color:'var(--text-muted)', flexShrink:0, textAlign:'right' },
    pagination: { display:'flex', gap:'8px', justifyContent:'center', marginTop:'20px' },
    pageBtn: (disabled) => ({ padding:'6px 16px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:disabled?'not-allowed':'pointer', background:'var(--surface)', border:'1px solid var(--border)', color:disabled?'var(--text-muted)':'var(--text)', opacity:disabled?0.5:1 }),
    empty: { textAlign:'center', padding:'48px', color:'var(--text-muted)', fontSize:'14px' },
  };

  const filterGroups = ['', 'stream', 'user', 'destination', 'recording', 'settings', 'apikey'];

  return (
    <NavBar>
      <div style={s.page}>
      <nav style={s.nav}>
        <div style={s.navLogo}><div style={s.navDot}/>MultiStream</div>
        <div style={s.navRight}>
          <button style={s.navBtn(false)} onClick={()=>navigate('/dashboard')}>Dashboard</button>
          <button style={s.navBtn(false)} onClick={()=>navigate('/settings')}>Settings</button>
          {user?.role==='admin'&&<button style={s.navBtn(true)} onClick={()=>navigate('/admin')}>Users</button>}
          <button style={s.navBtn(false)} onClick={logout}>Sign out</button>
        </div>
      </nav>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Activity Log</div>
            <div style={{fontSize:'14px',color:'var(--text-muted)',marginTop:'4px'}}>{total} total events</div>
          </div>
          <div style={s.filters}>
            {filterGroups.map(f=>(
              <button key={f} style={s.filterBtn(filter===f)} onClick={()=>setFilter(f)}>
                {f||'All'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{color:'var(--text-muted)',padding:'32px 0'}}>Loading…</div>
        ) : logs.length === 0 ? (
          <div style={s.empty}>No activity yet</div>
        ) : (
          <div style={s.logList}>
            {logs.map(log => {
              const meta = getActionMeta(log.action);
              return (
                <div key={log.id} style={s.logRow}>
                  <div style={s.logIcon(meta.color)}>{meta.icon}</div>
                  <div style={s.logInfo}>
                    <div style={s.logAction(meta.color)}>{meta.label}</div>
                    <div style={s.logDetail}>
                      {log.user_name && <strong>{log.user_name}</strong>}
                      {log.detail && ` · ${formatDetail(log.detail)}`}
                    </div>
                  </div>
                  <div style={s.logMeta}>
                    <div>{new Date(log.created_at).toLocaleDateString()}</div>
                    <div>{new Date(log.created_at).toLocaleTimeString()}</div>
                    {log.ip && <div style={{fontSize:'10px',fontFamily:'var(--mono)'}}>{log.ip}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {total > LIMIT && (
          <div style={s.pagination}>
            <button style={s.pageBtn(offset===0)} disabled={offset===0} onClick={()=>setOffset(Math.max(0,offset-LIMIT))}>← Prev</button>
            <span style={{padding:'6px 12px',fontSize:'13px',color:'var(--text-muted)'}}>
              {offset+1}–{Math.min(offset+LIMIT,total)} of {total}
            </span>
            <button style={s.pageBtn(offset+LIMIT>=total)} disabled={offset+LIMIT>=total} onClick={()=>setOffset(offset+LIMIT)}>Next →</button>
          </div>
        )}
      </div>
    </div>
    </NavBar>
  );
}
